import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Calendar } from "lucide-react";
import BackButton from "@/components/buttons/BackButton";
import useHeaderTitle from "@/hooks/useHeaderTitle";
import { zodResolver } from "@hookform/resolvers/zod";
import { kraManagementSchema } from "@/helpers/validation-schemas/kra-management/kra-management.schema";
import { z } from "zod";
import SelectField from "@/components/form/SelectField";
import { useNavigate, useParams } from "react-router-dom";
import { useCreateKRAMutation } from "@/states/kra-management/kra.api";
import TKRA from "@/types/kra-management/TKRA";
import { Toast } from "@/helpers/popups/Toast";
import kraManagementRoute from "@/utils/constants/kra-management/kra-management-route.constant";
import { useErrorHandling } from "@/hooks/useErrorHandling";
import { castTypeArr } from "@/utils/general/cast-type";
import { TFailResponse } from "@/types/general/TFailResponse";
import {
  useGetAllDepartmentQuery,
  useGetAllEmployeeDeparmentQuery,
  useLazyGetAllEmployeeQuery,
} from "@/states/common/common.api";
import { teamManagementSchema } from "@/helpers/validation-schemas/team-management/team-management.schema";
import {
  useCreateTeamMutation,
  useGetEditTeamQuery,
} from "@/states/team-management/team.api";
import CustomDateField from "@/components/form/CustomDateField";
import { hrActionManagementSchema } from "@/helpers/validation-schemas/hr-action-management/hr-action-management.schema";
import {
  useCreateHrActionMutation,
  useGetEditHrActionQuery,
  useUpdateHrActionMutation,
} from "@/states/hr-action-management/hr-action.api";
import hrActionRoute from "@/utils/constants/hr-action/hr-action-route.constant";
import { LoadingSpinner } from "@/components/layouts/LoadingSpinner";

type schemaType = z.infer<typeof hrActionManagementSchema>;

// Status options
const actionTypeOptions = [
  { value: "FINEDEDUCTION", label: "FINEDEDUCTION" },
  { value: "WARNING", label: "WARNING" },
];

const actionSourceOptions = [
  { value: "POLICY", label: "POLICY" },
  { value: "SOP", label: "SOP" },
];

const HrActionManagementEditPage: React.FC = () => {
  useHeaderTitle("Edit HR Action");
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<schemaType>({
    resolver: zodResolver(hrActionManagementSchema),
  });

  const { data: departmentData } = useGetAllDepartmentQuery();
  const { data: allDepartmentEmployeeData } = useGetAllEmployeeDeparmentQuery();
  console.log("departmentData", departmentData);

  const [
    getAllEmployee,
    {
      data: employeeData,
      error: employeeDataError,
      isLoading: employeeDataLoading,
    },
  ] = useLazyGetAllEmployeeQuery();
  const [
    updateHrAction,
    { error: updateError, isLoading: updateLoading, isSuccess },
  ] = useUpdateHrActionMutation();
  const { data: editData, isFetching: isEditFetching } =
    useGetEditHrActionQuery(id!, {
      skip: !id,
    });
  console.log("editData", editData);

  useEffect(() => {
    if (editData?.data && id) {
      const {
        action_type,
        action_source,
        amount,
        remark,
        date,
        employee_name,
      } = editData.data;

      reset({
        action_type: action_type,
        action_source: action_source,
        amount: parseFloat(amount), // convert string to number
        remark: remark || "",
        //@ts-ignore
        date: date ? new Date(date).toISOString().split("T")[0] : "", // format for input[type="date"]
        //@ts-ignore
        user_id: allDepartmentEmployeeData?.data?.find(
          (emp: any) => emp.employee_name == employee_name
        )?.user_id,
      });
    }
  }, [editData, id, reset, allDepartmentEmployeeData]);

  const onFormSubmit = async (data: schemaType) => {
    try {
      const finalData = {
        ...data,
        date: new Date(data.date as any), // cast string to Date
      };
      console.log("finalData", finalData);
      const res = await updateHrAction({
        //@ts-ignore
        id: id,
        data,
      });
      if (res?.data?.success) {
        Toast.fire({
          title: res.data?.message,
          icon: "success",
        });

        navigate(hrActionRoute.BASE_PATH);
      }
    } catch (err) {
      Toast.fire({
        title: "Fail to create new KRA",
        icon: "error",
      });
    }
  };

  useErrorHandling(...castTypeArr<TFailResponse>([updateError]));

  return (
    <div className="mx-auto">
      {isEditFetching && <LoadingSpinner />}
      <BackButton />
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="space-y-6 p-6 bg-white rounded-2xl"
      >
        {/* Date Fields Row */}

        {/* KRA Code and Detail Name Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Status */}
          <div className="space-y-2">
            <label
              htmlFor="dept_id"
              className="block text-sm font-medium text-gray-700"
            >
              Action Type
            </label>
            <SelectField
              name="action_type"
              control={control}
              options={
                //@ts-ignore
                actionTypeOptions
              }
              placeholder="Select Action Type"
            />
            {errors.action_type && (
              <p className="text-sm text-red-600">
                {errors.action_type.message}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <label
              htmlFor="action_source"
              className="block text-sm font-medium text-gray-700"
            >
              Action Source
            </label>
            <SelectField
              name="action_source"
              control={control}
              // employeeData
              options={
                //@ts-ignore
                actionSourceOptions
              }
              // placeholder={
              //   employeeDataLoading ? "Loading employees..." : "Select HOD"
              // }
            />
            {errors.action_source && (
              <p className="text-sm text-red-600">
                {
                  //@ts-ignore
                  errors.action_source.message
                }
              </p>
            )}
          </div>
          <div className="space-y-2">
            <label
              htmlFor="amount"
              className="block text-sm font-medium text-gray-700"
            >
              Amount
            </label>
            <input
              type="number"
              id="amount"
              {...register("amount")}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter Amount Name"
            />
            {errors.amount && (
              <p className="text-sm text-red-600">{errors.amount.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label
              htmlFor="date"
              className="block text-sm font-medium text-gray-700"
            >
              Date
            </label>
            <input
              type="date"
              id="date"
              {...register("date")}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter Date"
            />
            {errors.date && (
              <p className="text-sm text-red-600">{errors.date.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label
              htmlFor="user_id"
              className="block text-sm font-medium text-gray-700"
            >
              Employee Name
            </label>
            <SelectField
              name="user_id"
              control={control}
              // employeeData
              options={
                //@ts-ignore
                (allDepartmentEmployeeData?.data?.map((d: any) => ({
                  label: d.employee_name,
                  value: d.user_id,
                })) as any) ?? []
              }
              // placeholder={
              //   employeeDataLoading ? "Loading employees..." : "Select HOD"
              // }
            />
            {errors.user_id && (
              <p className="text-sm text-red-600">
                {
                  //@ts-ignore
                  errors.user_id.message
                }
              </p>
            )}
          </div>
          <div className="space-y-2">
            <label
              htmlFor="remark"
              className="block text-sm font-medium text-gray-700"
            >
              Remark
            </label>
            <input
              type="text"
              id="remark"
              {...register("remark")}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter Remark "
            />
            {errors.remark && (
              <p className="text-sm text-red-600">{errors.remark.message}</p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-start space-x-4 pt-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting || updateLoading ? "Update..." : "Update"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default HrActionManagementEditPage;
